import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@/convex/_generated/api';
import type { Id } from '@/convex/_generated/dataModel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
	X,
	ThumbsUp,
	HelpCircle,
	Wallet,
	ChevronUp,
	ChevronDown,
} from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { Checkbox } from '@/components/ui/checkbox';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAuth } from '@/hooks/useAuth';
import { useWeb3Auth } from '@/providers/Web3AuthProvider';
import { sendGaslessKarma, getKarmaBalance } from '@/lib/member-karma-wallet';
import { diagnosticFailureClass } from '@/lib/value-free-diagnostics';

interface SimpleTippingModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: (txHash: string, amount: string) => void;
	recipientName: string;
	recipientAddress: string;
	contentHistoryId?: Id<'contentHistory'>; // For post-specific tipping analytics
}

export function SimpleTippingModal({
	isOpen,
	onClose,
	onSuccess,
	recipientName,
	recipientAddress,
	contentHistoryId,
}: SimpleTippingModalProps): React.ReactPortal | null {
	const { user } = useAuth();
	const { memberSigner, walletAddress } = useWeb3Auth();

	const [amount, setAmount] = useState('0.33');
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [saveAsDefault, setSaveAsDefault] = useState(false);
	const [hasDefaultTip, setHasDefaultTip] = useState(false);
	const [karmaBalance, setKarmaBalance] = useState<string>('0');
	const [isCheckingBalance, setIsCheckingBalance] = useState(false);

	// Get user's current default tip amount
	const defaultTipAmount = useQuery(
		api.users.getDefaultTipAmount,
		user?.id ? { userId: user.id as unknown as Id<'users'> } : 'skip'
	);

	// Mutation to set default tip amount
	const setDefaultTipMutation = useMutation(api.users.setDefaultTipAmount);

	// Get recipient's wallet address from the database
	// Recipient user data available for future use
	useQuery(
		api.users.getUserByWalletAddress,
		recipientAddress ? { walletAddress: recipientAddress } : 'skip'
	);

	// Load default tip amount when modal opens
	useEffect(() => {
		if (isOpen && defaultTipAmount !== undefined) {
			if (defaultTipAmount !== null) {
				setAmount(defaultTipAmount.toString());
				setSaveAsDefault(true);
				setHasDefaultTip(true);
			} else {
				setAmount('0.33');
				setSaveAsDefault(false);
				setHasDefaultTip(false);
			}
		}
	}, [isOpen, defaultTipAmount]);

	// Check KARMA balance when modal opens
	useEffect(() => {
		if (isOpen && memberSigner) {
			setIsCheckingBalance(true);
			getKarmaBalance(memberSigner)
				.then((balance) => {
					// Balance is already in human-readable format from getKarmaBalance
					setKarmaBalance(balance);
				})
				.catch((err) => {
					console.error('Failed to get KARMA balance:', {
						failureClass: diagnosticFailureClass(err),
					});
					setKarmaBalance('0');
				})
				.finally(() => {
					setIsCheckingBalance(false);
				});
		}
	}, [isOpen, memberSigner]);

	// Function to handle amount changes and checkbox logic
	const handleAmountChange = (newAmount: string) => {
		setAmount(newAmount);

		// If user changes amount to something different from default, uncheck the checkbox
		if (defaultTipAmount !== undefined && defaultTipAmount !== null) {
			const newAmountNum = parseFloat(newAmount);
			if (!isNaN(newAmountNum) && newAmountNum !== defaultTipAmount) {
				setSaveAsDefault(false);
			} else if (newAmountNum === defaultTipAmount) {
				setSaveAsDefault(true);
			}
		}
	};

	// Increment amount by 0.01
	const incrementAmount = () => {
		const currentAmount = parseFloat(amount) || 0;
		const newAmount = (currentAmount + 0.01).toFixed(2);
		handleAmountChange(newAmount);
	};

	// Decrement amount by 0.01
	const decrementAmount = () => {
		const currentAmount = parseFloat(amount) || 0;
		const newAmount = Math.max(0, currentAmount - 0.01).toFixed(2);
		handleAmountChange(newAmount);
	};

	const handleSetDefaultChange = (checked: boolean | 'indeterminate') => {
		if (typeof checked !== 'boolean' || !user?.id) return;

		setSaveAsDefault(checked);
		const amountNum = parseFloat(amount);

		if (checked) {
			// User is checking the box, save the default amount
			if (amountNum > 0) {
				setDefaultTipMutation({
					userId: user.id as unknown as Id<'users'>,
					defaultTipAmount: amountNum,
				}).then(() => {
					setHasDefaultTip(true);
					toast({
						title: '✅ Default Tip Saved',
						description: `Your default tip is now ${amountNum} $KARMA.`,
					});
				});
			}
		} else {
			// User is unchecking the box, clear the default amount.
			setDefaultTipMutation({
				userId: user.id as unknown as Id<'users'>,
				defaultTipAmount: null,
			}).then(() => {
				setHasDefaultTip(false);
				toast({
					title: 'ⓘ Default Tip Cleared',
					description: 'Your default tip amount has been removed.',
				});
			});
		}
	};

	if (!isOpen) return null;

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);

		try {
			const amountNum = parseFloat(amount);
			if (amountNum <= 0) {
				throw new Error('Amount must be greater than 0');
			}

			if (!recipientAddress || !recipientAddress.startsWith('0x')) {
				throw new Error('Invalid recipient address');
			}

			// Check if user has a connected wallet
			if (!memberSigner) {
				throw new Error('Please connect your wallet first');
			}

			// Check balance
			const balanceNum = parseFloat(karmaBalance);
			if (amountNum > balanceNum) {
				throw new Error(
					`Insufficient KARMA balance. You have ${karmaBalance} KARMA`
				);
			}

			// Send the tip through our backend for analytics tracking
			const result = await sendGaslessKarma(
				memberSigner,
				recipientAddress,
				amount,
				contentHistoryId // Pass contentHistoryId for post-specific analytics
			);

			// Success!
			toast({
				title: '✨ Tip Sent Successfully!',
				description: `You tipped ${recipientName} ${amount} KARMA. Transaction: ${result.transactionHash}`,
				duration: 5000,
			});

			if (onSuccess && result.transactionHash) {
				onSuccess(result.transactionHash, amount);
			}

			// Update balance after successful transaction
			setTimeout(() => {
				if (memberSigner) {
					getKarmaBalance(memberSigner).then((balance) => {
						// Balance is already in human-readable format from getKarmaBalance
						setKarmaBalance(balance);
					});
				}
			}, 2000);

			onClose();
		} catch (err) {
			console.error('Failed to send tip:', {
				failureClass: diagnosticFailureClass(err),
			});
			const errorMessage =
				err instanceof Error ? err.message : 'Failed to send tip';
			setError(errorMessage);
			toast({
				title: 'Tip Failed',
				description: errorMessage,
				variant: 'destructive',
			});
		} finally {
			setIsLoading(false);
		}
	};

	const modalContent = (
		<div className='fixed inset-0 z-[9999] overflow-hidden'>
			<div
				className='fixed inset-0 bg-black/80 backdrop-blur-sm'
				onClick={onClose}
			/>

			<div className='fixed left-[50%] top-[50%] z-[10000] w-full max-w-sm translate-x-[-50%] translate-y-[-50%] duration-200'>
				<div className='shadow-[0_0_30px_5px_rgba(236,72,153,0.4)] border border-pink-500/50 bg-gradient-to-br from-zinc-900 via-zinc-900 to-purple-900/50 overflow-hidden rounded-2xl'>
					{/* Header */}
					<div className='flex items-center justify-between p-6 border-b border-pink-500/20'>
						<div className='flex items-center gap-3'>
							<div className='p-4 rounded-full bg-gradient-to-r from-pink-500 to-purple-600'>
								<ThumbsUp className='h-6 w-6 text-white' />
							</div>
							<div>
								<h2 className='text-xl font-bold text-white'>
									Send Tip To:
								</h2>
								<p className='text-pink-500 text-3xl font-bold'>
									{recipientName}
								</p>
								<span className='text-zinc-400 text-sm'>
									Wallet:{' '}
									<span className='text-white ml-3 font-mono'>
										{recipientAddress.slice(0, 6)}...
										{recipientAddress.slice(-4)}
									</span>
								</span>
							</div>
						</div>
						<Button
							variant='ghost'
							size='sm'
							onClick={onClose}
							className='text-pink-300 hover:text-white hover:bg-pink-500/20 transition-all duration-200 h-8 w-8 p-0'
							disabled={isLoading}
						>
							<X className='h-5 w-5' />
						</Button>
					</div>

					{/* Form */}
					<form onSubmit={handleSubmit} className='p-6 space-y-6'>
						{/* Wallet Status */}
						{walletAddress && (
							<div className='flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-zinc-800/50 to-zinc-900/50 border border-zinc-700'>
								<div className='flex items-center gap-2'>
									<Wallet className='h-4 w-4 text-zinc-400' />
									<span className='text-sm text-zinc-400'>
										Your wallet:
									</span>
								</div>
								<div className='flex items-center gap-6'>
									<span className='text-sm font-mono text-white'>
										{walletAddress.slice(0, 6)}...
										{walletAddress.slice(-4)}
									</span>
									<span className='text-xs text-pink-400'>
										{isCheckingBalance
											? 'Loading...'
											: `${karmaBalance} KARMA`}
									</span>
								</div>
							</div>
						)}

						{/* Amount Input */}
						<div className='space-y-3'>
							<div className='flex items-center gap-2'>
								<Label className='text-lg font-semibold text-white'>
									Tip Amount
								</Label>
								<TooltipProvider delayDuration={0}>
									<Tooltip>
										<TooltipTrigger asChild>
											<div className='h-4 w-4 rounded-full bg-gradient-to-r from-pink-500 to-pink-700 border border-white/20 flex items-center justify-center cursor-help'>
												<HelpCircle className='h-2.5 w-2.5 text-white' />
											</div>
										</TooltipTrigger>
										<TooltipContent className='bg-zinc-800 border-zinc-700 text-white max-w-xs'>
											<p className='text-sm'>
												Send $KARMA tokens directly from
												your wallet to support this
												creator's content. No gas fees
												required!
											</p>
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							</div>

							<div className='relative flex items-center'>
								{/* Increment/Decrement buttons */}
								<div className='flex flex-col mr-3'>
									<Button
										type='button'
										variant='ghost'
										size='sm'
										onClick={incrementAmount}
										disabled={isLoading}
										className='h-7 w-8 p-0 text-pink-300 hover:text-white hover:bg-pink-500/20 transition-all duration-200 rounded-t-lg rounded-b-none'
									>
										<ChevronUp className='h-4 w-4' />
									</Button>
									<Button
										type='button'
										variant='ghost'
										size='sm'
										onClick={decrementAmount}
										disabled={isLoading}
										className='h-7 w-8 p-0 text-pink-300 hover:text-white hover:bg-pink-500/20 transition-all duration-200 rounded-b-lg rounded-t-none'
									>
										<ChevronDown className='h-4 w-4' />
									</Button>
								</div>

								{/* Input field */}
								<div className='relative flex-1'>
									<Input
										type='number'
										value={amount}
										onChange={(e) =>
											handleAmountChange(e.target.value)
										}
										placeholder='0.33'
										className='text-xl h-14 bg-gradient-to-r from-zinc-800/70 to-zinc-900/70 border-pink-500/30 hover:border-pink-400/50 focus:border-pink-400 text-white placeholder:text-zinc-400 pr-20 rounded-xl [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]'
										disabled={isLoading}
										min='0'
										step='0.01'
									/>
									<div
										className='absolute right-4 top-1/2 -translate-y-1/2 text-pink-300 font-semibold text-lg select-none pointer-events-none cursor-default'
										style={{
											WebkitUserSelect: 'none',
											WebkitTouchCallout: 'none',
											userSelect: 'none',
										}}
									>
										$KARMA
									</div>
								</div>
							</div>
						</div>

						{/* Default Tip Checkbox */}
						<div className='flex items-center space-x-3 p-4 rounded-xl bg-gradient-to-r from-pink-500/10 to-purple-600/10 border border-pink-500/20'>
							<Checkbox
								id='saveAsDefault'
								checked={saveAsDefault}
								onCheckedChange={handleSetDefaultChange}
								className='h-5 w-5 border-pink-400 data-[state=checked]:bg-gradient-to-r data-[state=checked]:from-pink-500 data-[state=checked]:to-purple-600 data-[state=checked]:border-pink-400'
							/>
							<div className='flex-1'>
								<Label
									htmlFor='saveAsDefault'
									className='text-white font-medium cursor-pointer flex items-center gap-2'
								>
									Set as my default tip amount
									<TooltipProvider delayDuration={0}>
										<Tooltip>
											<TooltipTrigger asChild>
												<div className='h-3 w-3 rounded-full bg-gradient-to-r from-pink-500 to-pink-700 border border-white/20 flex items-center justify-center cursor-help'>
													<HelpCircle className='h-2 w-2 text-white' />
												</div>
											</TooltipTrigger>
											<TooltipContent className='bg-zinc-800 border-zinc-700 text-white max-w-xs'>
												<p className='text-sm'>
													Save this amount as your
													default for future tips. You
													can change this anytime.
												</p>
											</TooltipContent>
										</Tooltip>
									</TooltipProvider>
								</Label>
								<p className='text-sm text-pink-300/70 mt-1'>
									{hasDefaultTip
										? 'Update your default tip amount'
										: 'Quick tip for future use'}
								</p>
							</div>
						</div>

						{/* Error Display */}
						{error && (
							<div className='p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm'>
								{error}
							</div>
						)}

						{/* Submit Button */}
						<Button
							type='submit'
							disabled={
								isLoading ||
								!parseFloat(amount) ||
								parseFloat(amount) <= 0 ||
								!memberSigner ||
								isCheckingBalance
							}
							className='w-full h-14 bg-gradient-to-r from-pink-500 via-pink-600 to-purple-600 hover:from-pink-600 hover:via-pink-700 hover:to-purple-700 text-white font-semibold text-lg shadow-[0_0_20px_rgba(236,72,153,0.4)] hover:shadow-[0_0_30px_rgba(236,72,153,0.6)] transition-all duration-300 rounded-xl'
						>
							{isLoading ? (
								<div className='flex items-center gap-2'>
									<div className='w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin' />
									Sending Tip...
								</div>
							) : !memberSigner ? (
								<div className='flex items-center gap-2'>
									<Wallet className='h-5 w-5' />
									Connect Wallet to Tip
								</div>
							) : (
								<div className='flex items-center gap-2'>
									<ThumbsUp className='h-5 w-5' />
									Send - {amount} $KARMA
								</div>
							)}
						</Button>

						{/* Gasless Info */}
						<p className='text-xs text-center text-zinc-400'>
							✨ No gas fees required.
							<br /> Tips instantly sent from your wallet.
						</p>
					</form>
				</div>
			</div>
		</div>
	);

	return createPortal(modalContent, document.body);
}
